import useIsMounted from "ismounted";
import { pick } from "lodash";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { DeepPartial, UnpackNestedValue, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { StepperContext } from "../../../../components/stepper/context";
import { AppStore } from "../../../../store";
import { CryptoActions } from "../../../../store/crypto/actions";
import { CryptoDTO } from "../../../../store/crypto/models/dto";
import { useYupValidationResolver } from "../../../../utils/utils";

type FormType = CryptoDTO.Requests.CreateFraudReport.T;
const { defaults, useFields } = CryptoDTO.Requests.CreateFraudReport;

export function useStepValidation<
  T extends keyof CryptoDTO.Requests.CreateFraudReport.T
>(
  fields: Array<T>,
  customDefaults?: Partial<CryptoDTO.Requests.CreateFraudReport.T>,
  postValidation?: () => Promise<void>
) {
  const { validation } = useFields();
  const { setValidation } = useContext(StepperContext);
  const resolver = useYupValidationResolver(validation.pick(fields));
  const isMounted = useIsMounted();

  const defaultData = useSelector<
    AppStore,
    AppStore["crypto"]["newReportForm"]
  >((store) => store.crypto.newReportForm);

  const form = useForm<Pick<FormType, T>>({
    resolver,
    defaultValues: pick(
      { ...defaults, ...customDefaults, ...defaultData },
      ...fields
    ) as DeepPartial<Pick<FormType, T>>,
  });

  const dispatch = useDispatch();

  const handleSubmitCallback = useCallback(
    async (data: Record<string, unknown>) => {
      dispatch(
        CryptoActions.setNewFormReportSection(data as Partial<FormType>)
      );
      if (postValidation) {
        await postValidation();
      }
    },
    [dispatch, postValidation]
  );

  /** Set up the validation for the form */
  useEffect(() => {
    setValidation(() => () =>
      new Promise<void>((res, rej) => {
        form.handleSubmit((data: Record<string, unknown>) => {
          handleSubmitCallback(data).then(res, rej);
        }, rej)();
      })
    );
    return () => {
      if (isMounted.current) {
        // We have to check if it's mounted otherwise it will clear current validation
        // This is because the prior page's clear validation is run after the new page's setValidation
        setValidation(() => () => Promise.resolve());
      }
    };
  }, [
    handleSubmitCallback,
    setValidation,
    form.handleSubmit,
    isMounted.current,
  ]);

  return form;
}
